import { defineComponent as _defineComponent } from 'vue'
import { computed, inject, ref } from 'vue'

import { validationErrorsSymbol } from './utils'
import CommandLocationSearch from '@/components/core/CommandLocationSearch.vue'
import { useSelectedTrip, useTripSummary } from '@/composables/trip'
import { LocationAutocompleteResult } from '@/types/trip'
import { TripSummaryAccommodation } from '@/types/accommodation'
import { TransportationValidationError } from '@/composables/transportation-validation'

type TripSummaryAccommodationWithLocation = Omit<
  TripSummaryAccommodation,
  'location' | 'waypoint'
> &
  Required<Pick<TripSummaryAccommodation, 'location' | 'waypoint'>>

export interface TransportationLocationSelectorProps {
  waypointId: string
  isArrivalTransportation?: boolean
  origin?: LocationAutocompleteResult
  destination?: LocationAutocompleteResult
  originPlaceholder?: string
  destinationPlaceholder?: string
  showSuffix?: boolean
  originSuffix?: string
  destinationSuffix?: string
  suffixPlaceholder?: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'TransportationLocationSelector',
  props: {
    waypointId: null,
    isArrivalTransportation: { type: Boolean },
    origin: null,
    destination: null,
    originPlaceholder: null,
    destinationPlaceholder: null,
    showSuffix: { type: Boolean },
    originSuffix: null,
    destinationSuffix: null,
    suffixPlaceholder: null
  },
  emits: ["update:origin", "update:destination", "update:originSuffix", "update:destinationSuffix"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'update:origin', value?: LocationAutocompleteResult): void
  (e: 'update:destination', value?: LocationAutocompleteResult): void
  (e: 'update:originSuffix', value?: string): void
  (e: 'update:destinationSuffix', value?: string): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  waypointId: string
  isArrivalTransportation?: boolean
  origin?: LocationAutocompleteResult
  destination?: LocationAutocompleteResult
  originPlaceholder?: string
  destinationPlaceholder?: string
  showSuffix?: boolean
  originSuffix?: string
  destinationSuffix?: string
  suffixPlaceholder?: string
};




const trip = useSelectedTrip()

const locationSelectorRef = ref<HTMLDivElement | null>(null)

// Internal location values ----------------------------------------------------
const internalOrigin = computed({
  get: () => props.origin,
  set: (value) => {
    emit('update:origin', value)
  },
})

const internalDestination = computed({
  get: () => props.destination,
  set: (value) => {
    emit('update:destination', value)
  },
})

// Internal suffix values ----------------------------------------------------
const internalOriginSuffix = computed({
  get: () => props.originSuffix,
  set: (value) => {
    emit('update:originSuffix', value)
  },
})
const internalDestinationSuffix = computed({
  get: () => props.destinationSuffix,
  set: (value) => {
    emit('update:destinationSuffix', value)
  },
})

// Waypoint data ---------------------------------------------------------------
const waypointIndex = computed(() => {
  const waypoints = trip.value.waypoints
  const index = waypoints.findIndex((w) => w._id === props.waypointId)

  if (index === -1) {
    throw new Error(`Waypoint with id ${props.waypointId} not found`)
  }

  return index
})

const { data: tripSummary } = useTripSummary(trip.value.id)

const originWaypoints = computed(() => {
  if (props.isArrivalTransportation) {
    return []
  }

  return [trip.value.waypoints[waypointIndex.value]]
})

const destinationWaypoints = computed(() => {
  if (props.isArrivalTransportation) {
    return [trip.value.waypoints[waypointIndex.value]]
  } else if (waypointIndex.value < trip.value.waypoints.length - 1) {
    return [
      trip.value.waypoints[waypointIndex.value],
      trip.value.waypoints[waypointIndex.value + 1],
    ]
  }

  return [trip.value.waypoints[waypointIndex.value], trip.value.waypoints[0]]
})

const originAccommodations = computed(() => {
  if (!tripSummary.value || props.isArrivalTransportation) {
    return []
  }

  return tripSummary.value.accommodation.filter(
    (a): a is TripSummaryAccommodationWithLocation =>
      a.waypoint?._id === trip.value.waypoints[waypointIndex.value]._id &&
      !!a.location,
  )
})

const destinationAccommodations = computed(() => {
  if (!tripSummary.value) {
    return []
  } else if (props.isArrivalTransportation) {
    return tripSummary.value.accommodation.filter(
      (a): a is TripSummaryAccommodationWithLocation =>
        a.waypoint?._id === trip.value.waypoints[waypointIndex.value]._id &&
        !!a.location,
    )
  } else if (waypointIndex.value < trip.value.waypoints.length - 1) {
    return tripSummary.value.accommodation.filter(
      (a): a is TripSummaryAccommodationWithLocation =>
        a.waypoint?._id === trip.value.waypoints[waypointIndex.value + 1]._id &&
        !!a.location,
    )
  }

  return []
})

// Validation state ------------------------------------------------------------
const validationErrors = inject(validationErrorsSymbol, ref(new Map()))

const validationErrorKeys: TransportationValidationError[] = [
  'missingOrigin',
  'missingDestination',
  'missingBothLocations',
  'invalidDepartureIATACode',
  'invalidArrivalIATACode',
] as const

const validationMessages = computed(() => {
  const messages: string[] = []
  validationErrors.value.forEach((message, key) => {
    if (validationErrorKeys.includes(key)) {
      messages.push(message)
    }
  })

  return messages
})

const hasValidationErrors = computed(() => {
  return validationMessages.value.length > 0
})

return { __sfc: true,props, emit, trip, locationSelectorRef, internalOrigin, internalDestination, internalOriginSuffix, internalDestinationSuffix, waypointIndex, tripSummary, originWaypoints, destinationWaypoints, originAccommodations, destinationAccommodations, validationErrors, validationErrorKeys, validationMessages, hasValidationErrors, CommandLocationSearch }
}

})